import PropTypes from 'prop-types';
import {connect as redux_connect} from 'react-redux';

import Controller from './controller';
import {InnerProvider} from './provider';

function missingParameter(msg) {
  throw new Error(`Missing required paramater: ${msg}`);
}

function isController(arg1) {
  return (
    !!arg1 &&
    (arg1 instanceof Controller ||
      typeof arg1.mapStateToProps === 'function' ||
      typeof arg1.mapDispatchToProps === 'function')
  );
}

/**
 * 4 possibile function arguments.
 * Controller
 * Controller, options
 * Component, Controller
 * Component, Controller, options
 *
 * @param {Object} arg1 - Component or Controller
 * @param {Object} arg2 - Controller or options
 * @param {Object} arg3 - options
 *  Options currently only takes handleOnError as param.  That handleOnError can equal boolean
 *  or a function.  If it's a function, then on error, handleOnError will be called for rendered
 *  component.
 *
 */
export function connect(arg1 = missingParameter('Component or Controller'), arg2, arg3) {
  let Comp;
  let controller;
  let options;
  if (isController(arg1)) {
    controller = arg1;
    options = arg2;
  } else {
    Comp = arg1;
    controller = isController(arg2) && arg2;
    options = arg3;
  }

  const connector = _connect(controller, options || {});
  if (Comp) {
    return connector(Comp);
  }

  return connector;
}

function _connect(controller, {handleOnError = true}) {
  return (Comp) => {
    if (!controller) {
      return Comp;
    }
    const config = controller.getExports ? controller.getExports() : controller;

    const toProps = [];

    // Add queue context for Comp
    Comp.contextTypes = {...Comp.contextTypes, ...InnerProvider.contextTypes};

    toProps.push(config.mapStateToProps ? config.mapStateToProps : null);
    toProps.push(config.mapDispatchToProps ? config.mapDispatchToProps : null);

    return withReducer(
      config.reducers,
      config.autoDisconnect
    )(withErrorBoundary(handleOnError)(redux_connect(...toProps)(Comp)));
  };
}

function withErrorBoundary(handleOnError) {
  return (Comp) => {
    if (!handleOnError) {
      return Comp;
    }
    const originalComponentDidCatch = Comp.prototype.componentDidCatch;
    const originalRender = Comp.prototype.render;
    const originalShouldComponentUpdate = Comp.prototype.shouldComponentUpdate;
    Comp.contextTypes = {...Comp.contextTypes, logger: PropTypes.object};
    Object.assign(Comp.prototype, {
      shouldComponentUpdate(nextProps, nextState) {
        // Capture current should update b/c we need to honor that.
        const shouldUpdate =
          originalShouldComponentUpdate && originalShouldComponentUpdate.call(this, nextProps, nextState);

        // If we have error we should update or if overridden shouldComponentUpdate is true.
        return this.state.__hasError__ !== nextState.__hasError__ || shouldUpdate;
      },

      componentDidCatch(error, info) {
        if (typeof originalComponentDidCatch === 'function') {
          originalComponentDidCatch.call(this, error, info);
        }
        this.setState({__hasError__: true});
        this.context.logger.error(error, info);
      },
      render() {
        const {__hasError__} = this.state;
        if (!__hasError__ && typeof originalRender === 'function') {
          return originalRender.apply(this);
        }
        if (typeof handleOnError === 'function') {
          return handleOnError(this.props);
        }

        return null;
      },
    });

    return Comp;
  };
}

function withReducer(reducers, autoDisconnect) {
  return (Comp) => {
    const originalWillMount = Comp.prototype.UNSAFE_componentWillMount;
    const originalWillUnMount = Comp.prototype.componentWillUnmount;

    Object.assign(Comp.prototype, {
      UNSAFE_componentWillMount(...args) {
        if (typeof originalWillMount === 'function') {
          originalWillMount.apply(this, args);
        }
        const store = this.context.store || this.props.store;
        if (store && store.registerReducer) {
          for (const type in reducers) {
            store.registerReducer(type, reducers[type]);
          }
        }
      },
    });

    if (autoDisconnect) {
      Object.assign(Comp.prototype, {
        componentWillUnmount(...args) {
          if (typeof originalWillUnMount === 'function') {
            originalWillUnMount.apply(this, args);
          }
          const store = this.context.store || this.props.store;
          if (store && store.unregisterReducer) {
            for (const type in reducers) {
              store.unregisterReducer(type, reducers[type]);
            }
          }
        },
      });
    }

    return Comp;
  };
}
